import React, { useCallback, useMemo, useState } from 'react';

import { ALINE_TOOL_NAMES } from '@aline/common';
import { questionRunApprovalInputSchema } from '@aline/common/question-plan';
import type { QuestionRunApprovalInput } from '@aline/common/question-plan';

import { DocumentFilterControls } from './DocumentFilterControls';
import {
  buildQuestionRunApprovalResponse,
  getExistingRunQuestions,
  getIncludedColumns,
  getRunDisabledReason,
  getRunPrimaryLabel,
  toggleExcludedIndex,
} from './question-plan-helpers';
import { QuestionColumnsEditor } from './QuestionColumnsEditor';
import { QuestionPlanActions } from './QuestionPlanActions';
import { QuestionPlanApprovalCard, QuestionPlanHeader } from './QuestionPlanApprovalCard';
import { RunSummary } from './RunSummary';
import { useQuestionRunCandidateCount } from './use-question-run-candidate-count';
import { useSubmitApprovalResponse } from './use-submit-approval-response';
import { ApprovalCard } from '../AskUser';

interface QuestionRunApprovalCardProps {
  approvalId: string;
  input: unknown;
  messageId: string;
  maxHeight?: number | null;
}

/**
 * The run an analysis asked for before it answers: questions that exist but
 * have not run on these documents, and questions the agent drafted for what
 * no question records yet. The user can leave drafts out; running creates the
 * ones they kept, runs everything, and the answer follows once it finishes.
 */
export function QuestionRunApprovalCard({
  approvalId,
  input,
  maxHeight,
  messageId,
}: QuestionRunApprovalCardProps) {
  const parsed = questionRunApprovalInputSchema.safeParse(input);
  if (!parsed.success)
    return (
      <ApprovalCard
        approvalId={approvalId}
        input={input}
        maxHeight={maxHeight}
        messageId={messageId}
        toolName={ALINE_TOOL_NAMES.runExtractionQuestionsTool}
      />
    );

  // Keyed by approval so a later proposal on the same message starts with fresh edits.
  return (
    <QuestionRunApprovalCardReady
      key={approvalId}
      approvalId={approvalId}
      input={parsed.data}
      maxHeight={maxHeight}
      messageId={messageId}
    />
  );
}

function QuestionRunApprovalCardReady({
  approvalId,
  input,
  maxHeight,
  messageId,
}: {
  approvalId: string;
  input: QuestionRunApprovalInput;
  messageId: string;
  maxHeight?: number | null;
}) {
  const { submit, submitting } = useSubmitApprovalResponse();
  const [excludedIndices, setExcludedIndices] = useState<Set<number>>(new Set());
  const [filterModel, setFilterModel] = useState(input.filterModel);
  const existingQuestions = useMemo(() => getExistingRunQuestions(input), [input]);
  const includedDraftCount = getIncludedColumns(input.newQuestions, excludedIndices).length;
  const documentCount = useQuestionRunCandidateCount({
    enabled: !submitting,
    filterModel,
    includeDrafts: includedDraftCount > 0,
    initialCount: input.documentCountAtProposal,
    initialFilterModel: input.filterModel,
    messageId,
    runProposalId: input.runProposalId,
  });
  const counts = useMemo(
    () => ({
      documentCount: documentCount.count,
      questionCount: existingQuestions.length + includedDraftCount,
    }),
    [documentCount.count, existingQuestions.length, includedDraftCount]
  );

  const handleToggleExclude = useCallback(
    (index: number) =>
      setExcludedIndices(previous => toggleExcludedIndex(input.newQuestions, previous, index)),
    [input.newQuestions]
  );

  const respond = useCallback(
    (action: 'run' | 'skip', largeRunConfirmed = false) =>
      void submit(
        buildQuestionRunApprovalResponse({
          action,
          approvalId,
          excludedIndices,
          filterModel,
          input,
          largeRunConfirmed,
          messageId,
        })
      ),
    [approvalId, excludedIndices, filterModel, input, messageId, submit]
  );
  const handleRun = useCallback(
    ({ largeRunConfirmed }: { largeRunConfirmed: boolean }) => respond('run', largeRunConfirmed),
    [respond]
  );
  const handleSkip = useCallback(() => respond('skip'), [respond]);

  return (
    <QuestionPlanApprovalCard
      editor={
        <QuestionColumnsEditor
          disabled={submitting}
          editableColumns={input.newQuestions}
          excludedIndices={excludedIndices}
          readOnlyColumns={existingQuestions}
          onToggleExclude={handleToggleExclude}
        />
      }
      footer={
        <QuestionPlanActions
          countFailed={documentCount.failed}
          counts={counts}
          disabled={submitting}
          primaryDisabledReason={getRunDisabledReason({
            input,
            isCounting: documentCount.loading,
            questionCount: counts.questionCount,
          })}
          primaryLabel={getRunPrimaryLabel(includedDraftCount)}
          onRun={handleRun}
          onSkip={handleSkip}
        />
      }
      header={<QuestionPlanHeader description={getRunDescription(input)} />}
      listActions={
        filterModel && (
          <DocumentFilterControls
            disabled={submitting}
            filterModel={filterModel}
            logicOperatorLocked={input.filterLogicOperatorLocked === true}
            summary={<RunSummary {...counts} />}
            onFilterModelChange={setFilterModel}
          />
        )
      }
      listLabel="Questions"
      maxHeight={maxHeight}
      summary={!filterModel && <RunSummary {...counts} />}
      title={getRunCardTitle(input)}
    />
  );
}

function getRunCardTitle(input: QuestionRunApprovalInput) {
  if (input.newQuestions.length) return 'AI wants to create and run questions';
  return 'AI wants to run questions';
}

function getRunDescription(input: QuestionRunApprovalInput) {
  if (input.origin === 'uncovered' && input.missingFacts?.length)
    return `No question records ${formatList(input.missingFacts)} yet. Aline drafted questions for it; leave out any you don't want, and it answers once the run finishes.`;
  return 'Some answers are missing on these documents. Aline runs the questions, then answers once the run finishes.';
}

function formatList(items: string[]) {
  if (items.length === 1) return items[0];
  return `${items.slice(0, -1).join(', ')} and ${items.at(-1)}`;
}
